// Square Web Payments SDK service for handling embedded payments
// Note: The Square Web Payments SDK is loaded via CDN script tag

// Type definitions for Square Web Payments SDK
declare global {
  interface Window {
    Square?: {
      payments: (applicationId: string, locationId?: string) => SquarePayments;
    };
  }
}

export interface SquarePayments {
  card(options?: CardOptions): Promise<Card>;
  applePay(): Promise<ApplePay>;
  googlePay(): Promise<GooglePay>;
  ach(): Promise<ACH>;
}

export interface CardOptions {
  style?: {
    input?: {
      color?: string;
      fontFamily?: string;
      fontSize?: string;
      fontWeight?: string;
      lineHeight?: string;
      placeholderColor?: string;
      backgroundColor?: string;
    };
    [selector: string]: any;
  };
}

export interface Card {
  attach(selector: string): Promise<void>;
  tokenize(): Promise<TokenResult>;
  destroy(): void;
}

export interface ApplePay {
  attach(selector: string): Promise<void>;
  tokenize(paymentRequest: ApplePayPaymentRequest): Promise<TokenResult>;
  destroy(): void;
}

export interface GooglePay {
  attach(selector: string): Promise<void>;
  tokenize(paymentRequest: GooglePayPaymentRequest): Promise<TokenResult>;
  destroy(): void;
}

export interface ACH {
  attach(selector: string): Promise<void>;
  tokenize(accountHolderName: string): Promise<TokenResult>;
  destroy(): void;
}

export interface TokenResult {
  status: 'OK' | 'CANCEL' | 'ERROR';
  token?: string;
  details?: any;
  errors?: Array<{
    category: string;
    code: string;
    detail: string;
    field?: string;
  }>;
}

export interface ApplePayPaymentRequest {
  countryCode: string;
  currencyCode: string;
  total: {
    label: string;
    amount: string;
  };
}

export interface GooglePayPaymentRequest {
  countryCode: string;
  currencyCode: string;
  total: {
    label: string;
    amount: string;
  };
}

export interface PaymentResult {
  success: boolean;
  paymentId?: string;
  subscriptionId?: string;
  customerId?: string;
  error?: string;
  details?: any;
}

export interface SubscriptionPlan {
  id: string;
  name: 'premium' | 'platinum';
  price: number;
  currency: 'USD';
  billingFrequency: 'MONTHLY' | 'YEARLY';
  features: string[];
}

export interface CustomerData {
  givenName?: string;
  familyName?: string;
  emailAddress: string;
  phoneNumber?: string;
  companyName?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    locality?: string;
    administrativeDistrictLevel1?: string;
    postalCode?: string;
    country?: string;
  };
}

// Square Sandbox Configuration
const SQUARE_CONFIG = {
  applicationId: 'sandbox-sq0idb-dWiYHu3TBO_adaCDDgDPcA',
  accessToken: 'EAAAl4_ikrZeo9rQMZ8IhT19Qi2gKeNJf9r2FaJPVDtF-_R7WrfzMHz02-DoaTp8',
  environment: 'sandbox' as const,
  locationId: 'MAIN', // Default location for sandbox
};

// Subscription plans configuration
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'premium-monthly',
    name: 'premium',
    price: 999, // $9.99 in cents
    currency: 'USD',
    billingFrequency: 'MONTHLY',
    features: [
      'Unlimited swipes',
      'See who likes you', 
      'Advanced filters',
      'Read receipts',
      'Incognito browsing',
      'Up to 8 photos'
    ]
  },
  {
    id: 'platinum-monthly', 
    name: 'platinum',
    price: 1999, // $19.99 in cents
    currency: 'USD',
    billingFrequency: 'MONTHLY',
    features: [
      'Everything in Premium',
      'Profile boost',
      'Priority support', 
      'Video calls',
      'Up to 20 photos',
      'Message before matching'
    ]
  }
];

class SquarePaymentService {
  private payments: SquarePayments | null = null;
  private isInitialized = false;

  constructor() {
    this.loadSquareSDK();
  }

  // Load Square Web Payments SDK from CDN
  private async loadSquareSDK(): Promise<void> {
    if (window.Square) {
      this.initializePayments();
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;
      
      script.onload = () => {
        this.initializePayments();
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Square Web Payments SDK'));
      };

      document.head.appendChild(script);
    });
  }

  // Initialize Square Payments instance
  private initializePayments(): void {
    if (!window.Square) {
      throw new Error('Square SDK not loaded');
    }

    this.payments = window.Square.payments(SQUARE_CONFIG.applicationId, SQUARE_CONFIG.locationId);
    this.isInitialized = true;
  }

  // Wait for initialization
  async waitForInitialization(): Promise<void> {
    if (this.isInitialized && this.payments) return;
    
    // If not initialized, try to initialize
    if (!this.isInitialized) {
      await this.loadSquareSDK();
    }
    
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      
      const checkInit = () => {
        attempts++;
        if (this.isInitialized && this.payments) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Square SDK initialization timeout'));
        } else {
          setTimeout(checkInit, 100);
        }
      };
      checkInit();
    });
  }

  // Create card payment form
  async createCardPayment(): Promise<Card> {
    await this.waitForInitialization();
    if (!this.payments) {
      throw new Error('Square Payments not initialized');
    }
    console.log('Creating card payment instance...');
    try {
      const card = await this.payments.card({
        style: {
          input: {
            color: '#3B4B54',
            fontSize: '16px'
          },
          '.input-container': {
            borderColor: '#E5E7EB',
            borderRadius: '6px'
          },
          '.input-container.is-focus': {
            borderColor: '#3B82F6'
          },
          '.input-container.is-error': {
            borderColor: '#EF4444'
          }
        }
      });
      console.log('Card payment instance created successfully');
      return card;
    } catch (error) {
      console.error('Failed to create card payment:', error);
      throw error;
    }
  }

  // Create Apple Pay payment form
  async createApplePayPayment(): Promise<ApplePay> {
    await this.waitForInitialization();
    if (!this.payments) {
      throw new Error('Square Payments not initialized');
    }
    return this.payments.applePay();
  }

  // Create Google Pay payment form
  async createGooglePayPayment(): Promise<GooglePay> {
    await this.waitForInitialization();
    if (!this.payments) {
      throw new Error('Square Payments not initialized');
    }
    return this.payments.googlePay();
  }

  // Create ACH payment form
  async createACHPayment(): Promise<ACH> {
    await this.waitForInitialization();
    if (!this.payments) {
      throw new Error('Square Payments not initialized');
    }
    return this.payments.ach();
  }

  // Process subscription payment
  async processSubscriptionPayment(
    token: string,
    planId: string,
    customerData: CustomerData
  ): Promise<PaymentResult> {
    try {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Invalid subscription plan');
      }

      // In a real implementation, this would make API calls to your backend
      // which would then use Square's Payments API to process the payment
      const paymentData = {
        source_id: token,
        amount_money: {
          amount: plan.price,
          currency: plan.currency
        },
        idempotency_key: this.generateIdempotencyKey(),
        autocomplete: true,
        customer: customerData,
        subscription_plan: plan
      };

      // Simulate API call to backend
      const result = await this.makeBackendAPICall('/api/payments/subscription', paymentData);

      return {
        success: true,
        paymentId: result.payment_id,
        subscriptionId: result.subscription_id,
        customerId: result.customer_id,
        details: result
      };

    } catch (error) {
      console.error('Subscription payment failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment failed'
      };
    }
  }

  // Process one-time payment
  async processOneTimePayment(
    token: string,
    amount: number,
    currency: string = 'USD',
    description?: string
  ): Promise<PaymentResult> {
    try {
      const paymentData = {
        source_id: token,
        amount_money: {
          amount,
          currency
        },
        idempotency_key: this.generateIdempotencyKey(),
        autocomplete: true,
        note: description
      };

      const result = await this.makeBackendAPICall('/api/payments/one-time', paymentData);

      return {
        success: true,
        paymentId: result.payment_id,
        details: result
      };

    } catch (error) {
      console.error('One-time payment failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment failed'
      };
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId: string): Promise<PaymentResult> {
    try {
      const result = await this.makeBackendAPICall(`/api/subscriptions/${subscriptionId}/cancel`, {}, 'POST');
      
      return {
        success: true,
        subscriptionId,
        details: result
      };

    } catch (error) {
      console.error('Subscription cancellation failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Cancellation failed'
      };
    }
  }

  // Update subscription
  async updateSubscription(subscriptionId: string, newPlanId: string): Promise<PaymentResult> {
    try {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === newPlanId);
      if (!plan) {
        throw new Error('Invalid subscription plan');
      }

      const result = await this.makeBackendAPICall(`/api/subscriptions/${subscriptionId}`, {
        plan_variation_id: newPlanId,
        price_override_money: {
          amount: plan.price,
          currency: plan.currency
        }
      }, 'PUT');

      return {
        success: true,
        subscriptionId,
        details: result
      };

    } catch (error) {
      console.error('Subscription update failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Update failed'
      };
    }
  }

  // Get subscription details
  async getSubscription(subscriptionId: string): Promise<any> {
    try {
      return await this.makeBackendAPICall(`/api/subscriptions/${subscriptionId}`, {}, 'GET');
    } catch (error) {
      console.error('Failed to get subscription:', error);
      throw error;
    }
  }

  // List customer subscriptions
  async getCustomerSubscriptions(customerId: string): Promise<any[]> {
    try {
      const result = await this.makeBackendAPICall(`/api/customers/${customerId}/subscriptions`, {}, 'GET');
      return result.subscriptions || [];
    } catch (error) {
      console.error('Failed to get customer subscriptions:', error);
      return [];
    }
  }

  // Generate unique idempotency key
  private generateIdempotencyKey(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Simulate backend API calls (replace with actual API implementation)
  private async makeBackendAPICall(endpoint: string, data: any, method: string = 'POST'): Promise<any> {
    // In a real implementation, this would be actual fetch calls to your backend
    // Your backend would use Square's server-side APIs with the access token
    
    console.log(`[DEMO] Backend API Call: ${method} ${endpoint}`, data);
    
    // Simulate API response delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return mock successful response
    return {
      payment_id: `payment_${this.generateIdempotencyKey()}`,
      subscription_id: `sub_${this.generateIdempotencyKey()}`,
      customer_id: `cust_${this.generateIdempotencyKey()}`,
      status: 'COMPLETED',
      created_at: new Date().toISOString(),
      amount_money: data.amount_money,
      source_type: 'CARD'
    };
  }

  // Validate card details
  static validateCardData(cardData: any): boolean {
    // Basic validation - in real implementation, Square SDK handles this
    return !!(cardData && cardData.token);
  }

  // Format amount for display
  static formatAmount(amountInCents: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amountInCents / 100);
  }

  // Get plan by ID
  static getPlan(planId: string): SubscriptionPlan | undefined {
    return SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
  }

  // Get configuration
  static getConfig() {
    return SQUARE_CONFIG;
  }
}

// Export singleton instance
export const squarePaymentService = new SquarePaymentService();
export default squarePaymentService;